import React, { useState, useEffect } from "react";
import Game from "./game";
import Instructions from "./instructions";
import Stats from "./stats";
import Login from "./login";
import ResetPassword from "./ResetPassword";
import Tutorial from "./components/Tutorial";
import { supabase } from "./supabase";
import ErrorBoundary from './components/ErrorBoundary';
import { isSoundEnabled, setSoundEnabled, getVolume, setVolume } from "./utils/sounds";
import { ITEMS_PER_DAY } from "./utils/gameHelpers";
import "./App.css";

function App() {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [user, setUser] = useState(null);

  // Per-slot hard mode tracking
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const hardModeKey = `erewhondle-hardModeSlots-${todayStr}`;

  // Once today's five are done, the link to Medori's Games says how it went (?erewhondle=4&on=2026-10-16), so its
  // Today shelf can show Erewhondle as played, with its streak. Read from what the game keeps on this device.
  const sayHowItWent = (event) => {
    try {
      const saved = JSON.parse(localStorage.getItem(`erewhondle-${todayStr}`) || "{}");
      const states = Object.values(saved.slotStates || {});
      const done = states.filter((state) => state && (state.status === "won" || state.status === "lost"));
      if (done.length < ITEMS_PER_DAY) return;
      const won = done.filter((state) => state.status === "won").length;
      event.currentTarget.href = `https://medori.games/?erewhondle=${won}&on=${todayStr}`;
    } catch {
      // Nothing kept, or unreadable: the plain link.
    }
  };

  const [hardModeSlots, setHardModeSlots] = useState(() => {
    try {
      const saved = localStorage.getItem(hardModeKey);
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  });
  const [activeSlot, setActiveSlot] = useState(1);
  const [activeSlotLocked, setActiveSlotLocked] = useState(false);
  const [darkMode, setDarkMode] = useState(() =>
    localStorage.getItem("erewhondle-darkMode") === "true"
  );
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());
  const [volume, setVolumeState] = useState(() => getVolume());
  const [showVolume, setShowVolume] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("erewhondle-darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem(hardModeKey, JSON.stringify(hardModeSlots));
  }, [hardModeSlots, hardModeKey]);

  const currentSlotHardMode = !!hardModeSlots[activeSlot];

  const toggleHardMode = () => {
    if (activeSlotLocked) return;
    setHardModeSlots((prev) => ({ ...prev, [activeSlot]: !prev[activeSlot] }));
  };

  // Show tutorial on first visit, instructions on subsequent visits
  useEffect(() => {
    if (!localStorage.getItem("seenTutorial")) {
      setShowTutorial(true);
    } else if (!localStorage.getItem("seenInstructions")) {
      setShowInstructions(true);
      localStorage.setItem("seenInstructions", "true");
    }
  }, []);

  // Detect password reset redirect
  useEffect(() => {
    if (window.location.hash === "#reset-password") {
      setShowResetPassword(true);
    }
  }, []);

  // Auth session handling
  useEffect(() => {
    const getSession = async () => {
      if (!supabase?.auth?.getSession) return;
      try {
        const result = await supabase.auth.getSession();
        const session = result?.data?.session;
        if (session?.user) {
          setUser(session.user);
        }
      } catch {
        // Ignore auth bootstrap errors in local/test environments.
      }
    };
    getSession();

    let subscription;
    if (supabase?.auth?.onAuthStateChange) {
      const result = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user || null);
      });
      subscription = result?.data?.subscription;
    }

    return () => {
      subscription?.unsubscribe?.();
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        {/* Back to Medori's Games, in this tab, at the top where it's found (Brandon: "make it easier to return to main
            menu from Erewhondle"). Says how today went, like the link at the foot. */}
        <a className="back-to-medori" href="https://medori.games" onClick={sayHowItWent}>
          <span aria-hidden="true">‹</span> Medori&apos;s Games
        </a>
        <div className="top-bar">
          <h1>Erewhondl<span className="title-accent">e</span><span className="title-dot">.</span></h1>

          {/* Desktop: all buttons in a row */}
          <div className="top-buttons-desktop">
            <div className="hard-mode-wrapper">
              <button
                className={`hard-mode-btn ${currentSlotHardMode ? "active" : ""}`}
                onClick={toggleHardMode}
                disabled={activeSlotLocked}
                title={activeSlotLocked ? "Locked after first guess" : currentSlotHardMode ? "Disable hard mode" : "Enable hard mode"}
                aria-label={activeSlotLocked ? "Hard mode locked" : currentSlotHardMode ? "Disable hard mode" : "Enable hard mode"}
              >
                🔥
              </button>
              <span className={`hard-mode-label ${currentSlotHardMode ? "active" : ""}`}>HARD</span>
            </div>
            <button onClick={() => setShowInstructions(true)} aria-label="How to play">?</button>
            <button onClick={() => setShowStats(true)} aria-label="View stats">📊</button>
            <button
              onClick={() => setDarkMode(!darkMode)}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? "☀️" : "🌙"}
            </button>
            <button
              onClick={() => setShowVolume((v) => !v)}
              aria-label={soundOn ? "Adjust volume" : "Unmute sounds"}
            >
              {soundOn ? (volume > 0.5 ? "🔊" : volume > 0 ? "🔉" : "🔇") : "🔇"}
            </button>
          </div>

          {/* Mobile: hamburger icon */}
          <button className="hamburger-btn" onClick={() => setShowMenu((m) => !m)} aria-label="Menu">
            {showMenu ? "✕" : "☰"}
          </button>
        </div>

        {/* Mobile dropdown menu */}
        {showMenu && (
          <div className="mobile-menu">
            <div className="mobile-menu-row">
              <div className="hard-mode-wrapper">
                <button
                  className={`hard-mode-btn ${currentSlotHardMode ? "active" : ""}`}
                  onClick={toggleHardMode}
                  disabled={activeSlotLocked}
                >
                  🔥
                </button>
                <span className={`hard-mode-label ${currentSlotHardMode ? "active" : ""}`}>HARD</span>
              </div>
              <button onClick={() => { setShowInstructions(true); setShowMenu(false); }}>?</button>
              <button onClick={() => { setShowStats(true); setShowMenu(false); }}>📊</button>
              <button onClick={() => setDarkMode(!darkMode)}>
                {darkMode ? "☀️" : "🌙"}
              </button>
              <button onClick={() => { setShowVolume(true); setShowMenu(false); }}>
                {soundOn ? (volume > 0.5 ? "🔊" : volume > 0 ? "🔉" : "🔇") : "🔇"}
              </button>
            </div>
          </div>
        )}

        {showTutorial && (
          <Tutorial onClose={() => {
            setShowTutorial(false);
            localStorage.setItem("seenTutorial", "true");
            localStorage.setItem("seenInstructions", "true");
            // Back to the real price box, ready to type.
            setTimeout(() => document.querySelector(".guess-input")?.focus(), 0);
          }} />
        )}

        {showInstructions && (
          <Instructions
            onClose={() => setShowInstructions(false)}
            onTutorial={() => { setShowInstructions(false); setShowTutorial(true); }}
          />
        )}

        {showResetPassword && (
          <ResetPassword onDone={() => {
            setShowResetPassword(false);
            window.location.hash = "";
          }} />
        )}

        {showStats && (
          user
            ? <Stats user={user} onClose={() => setShowStats(false)} />
            : <Login onLogin={setUser} onClose={() => setShowStats(false)} />
        )}

        {showVolume && (
          <div className="volume-overlay" onClick={() => setShowVolume(false)}>
            <div className="volume-modal" onClick={(e) => e.stopPropagation()}>
              <div className="volume-modal-row">
                <button
                  className="volume-mute-btn"
                  onClick={() => { const next = !soundOn; setSoundOn(next); setSoundEnabled(next); }}
                >
                  {soundOn ? "🔊" : "🔇"}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setVolumeState(v);
                    setVolume(v);
                    if (v > 0 && !soundOn) { setSoundOn(true); setSoundEnabled(true); }
                    if (v === 0) { setSoundOn(false); setSoundEnabled(false); }
                  }}
                  className="volume-slider"
                />
                <span className="volume-pct">{Math.round(volume * 100)}%</span>
              </div>
            </div>
          </div>
        )}

        {/* While the tutorial or the instructions are up, the real game behind them can't be typed into: its
            price box takes the keyboard as the page loads, so a first visitor's typing went into the real game. */}
        <div inert={showTutorial || showInstructions}>
          <Game
            user={user}
            hardModeSlots={hardModeSlots}
            soundOn={soundOn}
            onSlotChange={(slot, locked) => {
              setActiveSlot(slot);
              setActiveSlotLocked(locked);
            }}
          />
        </div>

        <p className="feedback-link more-games">
          More games at{" "}
          <a href="https://medori.games" target="_blank" rel="noopener noreferrer" onClick={sayHowItWent}>
            Medori&apos;s Games
          </a>
        </p>
      </div>
    </ErrorBoundary>
  );
}

export default App;
